import { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { subscriberChannel } from '@/lib/realtime'
import { useAuthStore } from '@/stores/auth'
import {
  getNotificationLink,
  formatNotificationBody,
  formatNotificationTitle,
  NOTIFICATION_TYPES_BY_CATEGORY,
} from '@/lib/notification-links'
import { showOsNotification } from '@/lib/browser-notifications'
import { playNotificationSound } from '@/lib/notification-sound'
import type { Database } from '@/lib/types/database'

type Notification = Database['public']['Tables']['notifications']['Row']

export type NotificationFilter = 'all' | 'unread' | 'offers' | 'events' | 'chat'

/* The three lists used to be declared again here, one file away from the copy
   in `notification-links.ts` that decides the icon and the link. They had
   already drifted: `share_link_first_view` was in neither, so the chip that
   should have shown it filtered it out. One declaration, imported. */
const { offers: OFFER_TYPES, events: EVENT_TYPES, chat: CHAT_TYPES } =
  NOTIFICATION_TYPES_BY_CATEGORY

interface UseNotificationsOptions {
  filter?: NotificationFilter
  /** Default 50. Pass a smaller number for the bell-popover. */
  limit?: number
  /** If true, fires sonner-toast + OS-notification on realtime INSERTs. Default true. */
  notifyOnArrival?: boolean
}

/**
 * The unread total, counted in the DATABASE and not in the loaded page.
 *
 * `unreadCount` used to be `notifications.filter(n => !n.read).length` over a
 * list capped at `limit`, which made it two different wrong numbers at once:
 * the header could never say more than «50 uleste» however many there were, and
 * on the «Tilbud» tab it silently switched meaning to «unread offers» while the
 * «Merk alle som lest» button beside it still marked EVERYTHING. The sidebar
 * bell, meanwhile, had always counted properly — so the same user could read two
 * different unread totals on one screen. One query, both readers.
 */
async function fetchUnreadCount(userId: string): Promise<number> {
  const { count } = await supabase
    .from('notifications')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .eq('read', false)
    .eq('archived', false)
  return count ?? 0
}

async function fetchSoundPreference(userId: string): Promise<boolean> {
  const { data } = await supabase
    .from('notification_preferences')
    .select('sound_enabled')
    .eq('user_id', userId)
    .maybeSingle()
  const sound = (data as { sound_enabled?: boolean } | null)?.sound_enabled
  return sound ?? true
}

export function useNotifications(
  filterOrOptions: NotificationFilter | UseNotificationsOptions = 'all'
) {
  const opts: UseNotificationsOptions =
    typeof filterOrOptions === 'string'
      ? { filter: filterOrOptions }
      : filterOrOptions
  const filter: NotificationFilter = opts.filter ?? 'all'
  const limit = opts.limit ?? 50
  const notifyOnArrival = opts.notifyOnArrival ?? true

  const [notifications, setNotifications] = useState<Notification[]>([])
  const [unreadCount, setUnreadCount] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const user = useAuthStore((s) => s.user)
  const navigate = useNavigate()
  const { t } = useTranslation()
  const soundPrefRef = useRef<boolean>(true)
  /* Monotonic request id. Mutations and realtime echoes can put two refetches in
     flight at once, and without this the SLOWER one wins and reinstates the
     state the user just changed. */
  const seqRef = useRef(0)

  /**
   * `silent` skips the loading flag. Every realtime event calls this, and with
   * the flag always set, one arriving row — or the echo of the user's own click
   * on «marker som lest» — swapped the whole list for a Skeleton and back. Same
   * rule the cover-image hook already carries: a refetch triggered by somebody
   * else's small change must not blank the list you are reading.
   */
  const fetchList = useCallback(async (silent = false) => {
    if (!user) {
      setLoading(false)
      return
    }
    const seq = ++seqRef.current
    if (!silent) setLoading(true)
    try {
      let query = supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('archived', false)
        .order('created_at', { ascending: false })
        .limit(limit)

      if (filter === 'unread') {
        query = query.eq('read', false)
      } else if (filter === 'offers') {
        query = query.in('type', OFFER_TYPES)
      } else if (filter === 'events') {
        query = query.in('type', EVENT_TYPES)
      } else if (filter === 'chat') {
        query = query.in('type', CHAT_TYPES)
      }

      const [{ data, error: err }, unread] = await Promise.all([
        query,
        fetchUnreadCount(user.id),
      ])
      if (seq !== seqRef.current) return
      setUnreadCount(unread)
      if (err) {
        setError(err.message)
      } else {
        setNotifications((data ?? []) as Notification[])
        setError(null)
      }
    } catch (err) {
      if (seq !== seqRef.current) return
      setError(err instanceof Error ? err.message : 'Unknown error')
    } finally {
      if (seq === seqRef.current) setLoading(false)
    }
  }, [user?.id, filter, limit])

  useEffect(() => {
    fetchList()
  }, [fetchList])

  // Load the user's sound preference once so realtime triggers know
  // whether to play the chime.
  useEffect(() => {
    if (!user?.id) return
    fetchSoundPreference(user.id).then((v) => {
      soundPrefRef.current = v
    })
  }, [user?.id])

  // Realtime — INSERT bumps the local list and fires UX side-effects.
  useEffect(() => {
    if (!user?.id) return
    const channel = subscriberChannel(`notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        (payload) => {
          const row = payload.new as Notification
          // Refresh the full list (preserves filtering/order) without blanking
          // what is on screen — see `fetchList`'s `silent`.
          void fetchList(true)

          if (!notifyOnArrival) return

          // Suppress for deferred (quiet-hours) rows
          if (row.deliver_after && new Date(row.deliver_after) > new Date()) return

          const link = getNotificationLink(row)
          const title = formatNotificationTitle(row, t)
          const body = formatNotificationBody(row, t) ?? ''

          playNotificationSound(soundPrefRef.current)

          toast(title, {
            description: body,
            action: {
              label: t('common.view', { defaultValue: 'Vis' }),
              onClick: () => navigate(link),
            },
          })

          showOsNotification(title, body, {
            tag: (row.metadata as { event_id?: string } | null)?.event_id,
            url: window.location.origin + link,
            onNavigate: navigate,
          })
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => void fetchList(true)
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id, fetchList, notifyOnArrival, navigate, t])

  /**
   * Every mutation below went `await …; setState(…)` with the `error` field
   * dropped on the floor. A write refused by RLS, or lost offline, therefore
   * looked exactly like a write that landed: the row greyed out, the count fell,
   * and the next refetch — a realtime echo, a filter change, a reload — put it
   * silently back. That is the shape the repo already has a rule about
   * («skriveren oppdaterer sin egen lista … og KASTER ved feil»): the receipt
   * has to belong to the write, not to the click.
   *
   * Each one now raises the toast the user can act on and re-reads the truth
   * rather than leaving the list asserting something the database never agreed
   * to. `throw`ing instead would take the page down with it; these are
   * background list edits, so the honest failure is «that did not happen» plus
   * the real state back on screen.
   */
  const guard = useCallback(
    async (
      /* PromiseLike, not Promise: a PostgREST builder is thenable but has no
         `catch`/`finally`, so `Promise<…>` rejects every call site. */
      run: () => PromiseLike<{ error: { message: string } | null }>,
      optimistic: () => void
    ) => {
      optimistic()
      const { error: err } = await run()
      if (err) {
        toast.error(t('common.error', { defaultValue: 'Noe gikk galt' }))
        void fetchList(true)
        return false
      }
      return true
    },
    [fetchList, t]
  )

  const markAsRead = useCallback(
    async (id: string) => {
      await guard(
        () => supabase.from('notifications').update({ read: true }).eq('id', id),
        () => {
          setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, read: true } : n))
          )
          setUnreadCount((c) => Math.max(0, c - 1))
        }
      )
    },
    [guard]
  )

  /**
   * Task 78. `notification-row.tsx` was drawn with an `onMarkUnread` prop and
   * nothing in the corpus passed it, because the hook had no counterpart —
   * the row carried an exit that was never connected to anything. The hook half
   * landed then; the MENU ITEM did not, so until 13.08.2026 this function had no
   * caller either and «marker som ulest» remained unreachable from both surfaces.
   */
  const markAsUnread = useCallback(
    async (id: string) => {
      await guard(
        () => supabase.from('notifications').update({ read: false }).eq('id', id),
        () => {
          setNotifications((prev) =>
            prev.map((n) => (n.id === id ? { ...n, read: false } : n))
          )
          setUnreadCount((c) => c + 1)
        }
      )
    },
    [guard]
  )

  const markAllAsRead = useCallback(async () => {
    if (!user) return
    await guard(
      () =>
        supabase
          .from('notifications')
          .update({ read: true })
          .eq('user_id', user.id)
          .eq('read', false),
      () => {
        setNotifications((prev) => prev.map((n) => ({ ...n, read: true })))
        setUnreadCount(0)
      }
    )
  }, [user?.id, guard])

  const autoMarkVisibleAsRead = useCallback(
    async (ids: string[]) => {
      if (!ids.length) return
      await guard(
        () => supabase.from('notifications').update({ read: true }).in('id', ids),
        () => {
          setNotifications((prev) =>
            prev.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n))
          )
          setUnreadCount((c) => Math.max(0, c - ids.length))
        }
      )
    },
    [guard]
  )

  /**
   * Archiving and deleting take rows OUT of the inbox, and `fetchUnreadCount`
   * only counts `archived = false` — so removing an unread row lowers the total
   * too. Dropping them from the list without that adjustment left the header
   * counting notifications that were no longer there.
   */
  const dropRows = useCallback((ids: string[]) => {
    setNotifications((prev) => {
      const goneUnread = prev.filter((n) => ids.includes(n.id) && !n.read).length
      if (goneUnread) setUnreadCount((c) => Math.max(0, c - goneUnread))
      return prev.filter((n) => !ids.includes(n.id))
    })
  }, [])

  const archive = useCallback(
    async (id: string) => {
      await guard(
        () => supabase.from('notifications').update({ archived: true }).eq('id', id),
        () => dropRows([id])
      )
    },
    [guard, dropRows]
  )

  const bulkArchive = useCallback(
    async (ids: string[]) => {
      if (!ids.length) return
      await guard(
        () => supabase.from('notifications').update({ archived: true }).in('id', ids),
        () => dropRows(ids)
      )
    },
    [guard, dropRows]
  )

  const remove = useCallback(
    async (id: string) => {
      await guard(
        () => supabase.from('notifications').delete().eq('id', id),
        () => dropRows([id])
      )
    },
    [guard, dropRows]
  )

  const bulkDelete = useCallback(
    async (ids: string[]) => {
      if (!ids.length) return
      await guard(
        () => supabase.from('notifications').delete().in('id', ids),
        () => dropRows(ids)
      )
    },
    [guard, dropRows]
  )

  return {
    notifications,
    loading,
    error,
    unreadCount,
    markAsRead,
    markAsUnread,
    markAllAsRead,
    autoMarkVisibleAsRead,
    archive,
    bulkArchive,
    remove,
    bulkDelete,
    refetch: fetchList,
  }
}

/**
 * Bare tallet — uten lista, uten lydene, uten toastene.
 *
 * ⚠️ ÉN INSTANS. Den het `useUnreadCount` og kjørte i BÅDE sidemenyen og
 * bunnmenyen, som er verre enn dobbelt arbeid: begge ba om emnet
 * `unread-count-<uid>`, og `supabase.channel()` gjenbruker en kanal som
 * allerede finnes. De delte altså abonnementet — og når bunnmenyen ble
 * avmontert (den gjør det hver gang du går inn i et arrangement) meldte den av
 * kanalen for sidemenyen også. Sidemenyens tall sto deretter stille resten av
 * økta. `subscriberChannel` lukker den fella for godt; `UnreadProvider` sørger
 * for at det uansett bare finnes én instans. Bruk `useUnread()`.
 */
export function useNotificationUnreadCountSource() {
  const [count, setCount] = useState(0)
  const user = useAuthStore((s) => s.user)

  useEffect(() => {
    if (!user) return

    const fetchCount = async () => {
      setCount(await fetchUnreadCount(user.id))
    }

    fetchCount()

    const channel = subscriberChannel(`unread-count-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${user.id}`,
        },
        () => fetchCount()
      )
      .subscribe()

    return () => {
      supabase.removeChannel(channel)
    }
  }, [user?.id])

  return count
}
